import { useEffect } from 'react'
import { globalEntitySearch, type EntitySearchProvider } from '@appic/command-palette'
import { listMissions } from '@/lib/api/missions'
import { listUsers } from '@/lib/api/users'
import { listHelpArticles } from '@/lib/api/help'

function missionsProvider(): EntitySearchProvider {
  return {
    id: 'missions',
    module: 'Scout',
    label: 'Missions',
    search: async (query, limit = 5) => {
      const q = query.toLowerCase()
      const missions = await listMissions()
      return missions
        .filter((m) =>
          m.title.toLowerCase().includes(q) ||
          m.region?.toLowerCase().includes(q) ||
          m.status.toLowerCase().includes(q),
        )
        .slice(0, limit)
        .map((m) => ({
          id: m.id,
          label: m.title,
          sublabel: [m.region, m.status].filter(Boolean).join(' · '),
          module: 'Scout',
          href: `/scout/missionen`,
        }))
    },
  }
}

function usersProvider(): EntitySearchProvider {
  return {
    id: 'users',
    module: 'Settings',
    label: 'Users',
    search: async (query, limit = 5) => {
      const q = query.toLowerCase()
      const users = await listUsers()
      return users
        .filter((u) =>
          u.fullName?.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.role?.toLowerCase().includes(q),
        )
        .slice(0, limit)
        .map((u) => ({
          id: u.id,
          label: u.fullName || u.email,
          sublabel: [u.role, u.email].filter(Boolean).join(' · '),
          module: 'Settings',
          href: `/settings/users`,
        }))
    },
  }
}

function helpProvider(): EntitySearchProvider {
  return {
    id: 'help',
    module: 'Help',
    label: 'Help Articles',
    search: async (query, limit = 5) => {
      const q = query.toLowerCase()
      const articles = await listHelpArticles()
      return articles
        .filter((a) =>
          a.title.toLowerCase().includes(q) ||
          a.content?.toLowerCase().includes(q),
        )
        .slice(0, limit)
        .map((a) => ({
          id: a.id,
          label: a.title,
          sublabel: 'Help',
          module: 'Help',
          href: `/scout/hilfe`,
        }))
    },
  }
}

/**
 * Registers entity search providers with the global Cmd+K command palette.
 * Call once from App.tsx. Each provider implements async search against its API.
 */
export function useEntityProviders() {
  useEffect(() => {
    const providers: EntitySearchProvider[] = [
      missionsProvider(),
      usersProvider(),
      helpProvider(),
    ]
    const unregisters = providers.map((p) => globalEntitySearch.register(p))
    return () => unregisters.forEach((fn) => fn())
  }, [])
}
